/**
 * Canonical Voice Template generation readiness (button + validation share semantics).
 * No silent Generate: if not ready, button is disabled with visible blockers.
 */

import type { TFunction } from 'i18next'
import type { VoiceTemplateSourceType } from './voiceTemplateProvider'
import { CANONICAL_TEMPLATE_NAME_LIMIT, type VoiceTemplateFieldErrors } from './voiceTemplateValidation'
import type { VoiceReferenceRecordingDraftStatus } from '../../../voiceReference/voiceReferenceRecordingDraftStore'

export type VoiceTemplateGenerationBlockerCode =
  | 'WORKSPACE_MISSING'
  | 'TEMPLATE_NAME_REQUIRED'
  | 'TEMPLATE_NAME_TOO_LONG'
  | 'PREVIEW_TEXT_REQUIRED'
  | 'VOICE_DESCRIPTION_REQUIRED'
  | 'VOICE_REFERENCE_REQUIRED'
  | 'VOICE_REFERENCE_UPLOADING'
  | 'VOICE_REFERENCE_UPLOAD_FAILED'
  | 'VOICE_REFERENCE_QUALITY_FAILED'
  | 'VOICE_RIGHTS_REQUIRED'
  | 'PROVIDER_UNAVAILABLE'
  | 'VOICE_CLONE_UNAVAILABLE'

export type VoiceTemplateGenerationBlocker = {
  code: VoiceTemplateGenerationBlockerCode
  field?: keyof VoiceTemplateFieldErrors
  message: string
}

export type VoiceTemplateGenerationReadiness = {
  ready: boolean
  blockers: VoiceTemplateGenerationBlocker[]
  fieldErrors: VoiceTemplateFieldErrors
  primaryBlocker: VoiceTemplateGenerationBlocker | null
}

export type VoiceTemplatePrimaryParamKey = 'language' | 'gender' | 'style' | 'scene'

/**
 * Mode-specific primary params. Clone/reference do not use AI synthesis params
 * (backend clone path hardcodes locale; brief gender/style/scene are AI design hints).
 */
export function getVoiceTemplateVisiblePrimaryParams(
  sourceType: VoiceTemplateSourceType,
): VoiceTemplatePrimaryParamKey[] {
  if (sourceType === 'AI_GENERATED') return ['language', 'gender', 'style', 'scene']
  return []
}

export function evaluateVoiceTemplateGenerationReadiness(
  args: {
    workspaceId: string | null | undefined
    projectId: string | null | undefined
    sourceType: VoiceTemplateSourceType
    voiceDescription: string
    previewText: string
    templateName: string
    referenceAssetId: string | null
    voiceRightsConfirmed: boolean
    draftStatus?: VoiceReferenceRecordingDraftStatus | null
    providerAvailable: boolean
    /** null = unknown/loading; false = clone not ready in runtime */
    voiceCloneRuntimeEnabled?: boolean | null
    voiceCloneBlockerCode?: string | null
  },
  t: TFunction<'canvas'>,
): VoiceTemplateGenerationReadiness {
  const blockers: VoiceTemplateGenerationBlocker[] = []
  const fieldErrors: VoiceTemplateFieldErrors = {}
  const name = args.templateName.trim()
  const preview = args.previewText.trim()
  const brief = args.voiceDescription.trim()

  if (!args.workspaceId || !args.projectId) {
    blockers.push({
      code: 'WORKSPACE_MISSING',
      message: t('voiceTemplate.errors.invalidInput'),
    })
  }

  if (!name) {
    const message = t('voiceTemplate.validation.templateNameRequired')
    fieldErrors.templateName = message
    blockers.push({ code: 'TEMPLATE_NAME_REQUIRED', field: 'templateName', message })
  } else if (name.length > CANONICAL_TEMPLATE_NAME_LIMIT) {
    const message = t('voiceTemplate.validation.templateNameTooLong', { max: CANONICAL_TEMPLATE_NAME_LIMIT })
    fieldErrors.templateName = message
    blockers.push({ code: 'TEMPLATE_NAME_TOO_LONG', field: 'templateName', message })
  }

  if (!preview) {
    const message = t('voiceTemplate.validation.previewTextRequired')
    fieldErrors.previewText = message
    blockers.push({ code: 'PREVIEW_TEXT_REQUIRED', field: 'previewText', message })
  }

  if (args.sourceType === 'AI_GENERATED' && !brief) {
    const message = t('voiceTemplate.validation.voiceDescriptionRequired')
    fieldErrors.voiceDescription = message
    blockers.push({ code: 'VOICE_DESCRIPTION_REQUIRED', field: 'voiceDescription', message })
  }

  if (args.sourceType === 'REFERENCE' || args.sourceType === 'CLONED') {
    if (!args.referenceAssetId) {
      const draft = args.draftStatus
      let code: VoiceTemplateGenerationBlockerCode = 'VOICE_REFERENCE_REQUIRED'
      let message = t('voiceTemplate.validation.referenceRequired')
      if (draft === 'uploading') {
        code = 'VOICE_REFERENCE_UPLOADING'
        message = t('voiceTemplate.referenceCapture.recording.uploadingReference')
      } else if (draft === 'upload_failed') {
        code = 'VOICE_REFERENCE_UPLOAD_FAILED'
        message = t('voiceTemplate.validation.referenceUploadFailed')
      } else if (draft === 'recorded_local' || draft === 'ready_local' || draft === 'analyzing') {
        code = 'VOICE_REFERENCE_UPLOADING'
        message = t('voiceTemplate.referenceCapture.recording.preparingReference')
      }
      fieldErrors.referenceAssetId = message
      blockers.push({ code, field: 'referenceAssetId', message })
    }
  }

  if (args.sourceType === 'CLONED' && !args.voiceRightsConfirmed) {
    const message = t('voiceTemplate.validation.consentRequired')
    fieldErrors.voiceRightsConfirmed = message
    blockers.push({ code: 'VOICE_RIGHTS_REQUIRED', field: 'voiceRightsConfirmed', message })
  }

  if (args.sourceType === 'CLONED' && args.voiceCloneRuntimeEnabled === false) {
    const blockerCode = args.voiceCloneBlockerCode
    let message = t('voiceTemplate.errors.cloneUnavailable')
    if (blockerCode === 'CREDENTIAL_MISSING' || blockerCode === 'PROVIDER_NOT_CONFIGURED') {
      message = t('voiceTemplate.errors.providerNotReady')
    }
    blockers.push({
      code: 'VOICE_CLONE_UNAVAILABLE',
      message,
    })
  }

  if (!args.providerAvailable) {
    blockers.push({
      code: 'PROVIDER_UNAVAILABLE',
      message: t('voiceTemplate.errors.serviceUnavailable'),
    })
  }

  return {
    ready: blockers.length === 0,
    blockers,
    fieldErrors,
    primaryBlocker: blockers[0] ?? null,
  }
}
